import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, DatePicker, Switch, message, Space, Divider, Row as AntRow, Col, Button } from 'antd';
import { CreateVoucherDto, DiscountType } from '../../common/voucher.interface';
import { 
    GiftOutlined, 
    DollarOutlined, 
    CalendarOutlined, 
    PlusOutlined, 
    CloseOutlined,
    FileTextOutlined,
    UserOutlined,
    SettingOutlined,
    PercentageOutlined,
    MoneyCollectOutlined
} from '@ant-design/icons';
import { confirmCloseModal } from '../modalEditBook/CModalConfirmClose';
import dayjs from 'dayjs';

interface CModalAddVoucherProps {
  visible: boolean;
  onCancel: () => void;
  onOk: (data: CreateVoucherDto) => void;
}

const CModalAddVoucher: React.FC<CModalAddVoucherProps> = ({
  visible,
  onCancel,
  onOk,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [discountType, setDiscountType] = useState<DiscountType>(DiscountType.PERCENTAGE);
  const [hasChanges, setHasChanges] = useState(false);
  const values = Form.useWatch([], form);

  useEffect(() => {
    const currentValues = form.getFieldsValue();
    const hasFormChanges = 
      currentValues.code ||
      currentValues.title ||
      currentValues.description ||
      currentValues.discountType !== DiscountType.PERCENTAGE ||
      currentValues.discountValue ||
      currentValues.minOrderValue ||
      currentValues.maxDiscountAmount ||
      currentValues.pointsRequired ||
      currentValues.usageLimit ||
      currentValues.usagePerUser ||
      currentValues.startDate ||
      currentValues.endDate ||
      currentValues.isActive !== true;
    
    setHasChanges(hasFormChanges);
  }, [values]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Map từ UI form values sang Backend Schema chính xác
      const voucherData: CreateVoucherDto = {
        // Required fields theo backend schema
        code: values.code,
        name: values.title,                                    // Map title -> name
        type: (values.pointsRequired && values.pointsRequired > 0) ? 'PAID' : 'FREE',
        discount: values.discountValue,                        // Map discountValue -> discount  
        quantity: values.usageLimit || 1,                      // Map usageLimit -> quantity
        startDate: values.startDate.toISOString(),
        endDate: values.endDate.toISOString(),
        
        // Optional fields
        description: values.description,
        discountType: values.discountType,
        minOrder: values.minOrderValue,                        // Map minOrderValue -> minOrder
        pointsCost: values.pointsRequired,                     // Map pointsRequired -> pointsCost
        isActive: values.isActive,
        
        // Frontend compatibility (để không phá UI)
        title: values.title,
        discountValue: values.discountValue,
        minOrderValue: values.minOrderValue,
        pointsRequired: values.pointsRequired,
        usageLimit: values.usageLimit,
        usagePerUser: values.usagePerUser,
      };

      // Chỉ thêm maxDiscount khi discountType là PERCENTAGE
      if (values.discountType === DiscountType.PERCENTAGE && values.maxDiscountAmount) {
        voucherData.maxDiscount = values.maxDiscountAmount;
        voucherData.maxDiscountAmount = values.maxDiscountAmount;
      }

      onOk(voucherData);
      resetForm();
    } catch (error) {
      message.error('Vui lòng kiểm tra lại thông tin');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    confirmCloseModal({
      hasChanges,
      onConfirm: () => {
        resetForm();
        onCancel();
      },
      title: 'Xác nhận đóng',
      content: 'Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn đóng modal thêm voucher?'
    });
  };

  const resetForm = () => {
    form.resetFields();
    setDiscountType(DiscountType.PERCENTAGE);
    setHasChanges(false);
  };

  const handleDiscountTypeChange = (value: DiscountType) => {
    setDiscountType(value);
    // Reset discount value when changing type
    form.setFieldsValue({ discountValue: undefined });
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      title={
        <Space>
          <PlusOutlined />
          <span>Thêm voucher mới</span>
        </Space>
      }
      className="voucher-add-modal"
      footer={null}
      width={800}
      destroyOnClose
      maskClosable={true}
    >
      <div className="modal-content">
        <Form
          form={form}
          layout="vertical"
          className="voucher-form"
          initialValues={{
            discountType: DiscountType.PERCENTAGE,
            isActive: true,
            usagePerUser: 1,
          }}
          onFinish={handleOk}
        >
          {/* Basic Information */}
          <Divider orientation="left">
            <Space>
              <FileTextOutlined style={{ color: 'var(--primary-color)' }} />
              <span>Thông tin cơ bản</span>
            </Space>
          </Divider>

          <AntRow gutter={[16, 12]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <GiftOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Mã voucher</span>
                  </Space>
                }
                name="code"
                rules={[
                  { required: true, message: 'Vui lòng nhập mã voucher' },
                  { min: 3, message: 'Mã voucher phải có ít nhất 3 ký tự' },
                  { max: 50, message: 'Mã voucher không được quá 50 ký tự' },
                ]}
              >
                <Input placeholder="VD: SUMMER2025" className="form-input" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Tiêu đề voucher</span>
                  </Space>
                }
                name="title"
                rules={[
                  { required: true, message: 'Vui lòng nhập tiêu đề voucher' },
                  { max: 255, message: 'Tiêu đề không được quá 255 ký tự' },
                ]}
              >
                <Input placeholder="VD: Ưu đãi mùa hè 2025" className="form-input" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space>
                    <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Mô tả voucher</span>
                  </Space>
                }
                name="description"
              >
                <Input.TextArea 
                  placeholder="Mô tả chi tiết về voucher..." 
                  className="form-textarea"
                  rows={3}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </Col>
          </AntRow>

          {/* Discount Configuration */}
          <Divider orientation="left">
            <Space>
              <DollarOutlined style={{ color: 'var(--primary-color)' }} />
              <span>Cấu hình giảm giá</span>
            </Space>
          </Divider>

          <AntRow gutter={[16, 12]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <SettingOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Loại giảm giá</span>
                  </Space>
                }
                name="discountType"
                rules={[{ required: true, message: 'Vui lòng chọn loại giảm giá' }]}
              >
                <Select 
                  placeholder="Chọn loại giảm giá"
                  className="form-select"
                  onChange={handleDiscountTypeChange}
                >
                  <Select.Option value={DiscountType.PERCENTAGE}>
                    <Space>
                      <PercentageOutlined />
                      Giảm theo phần trăm
                    </Space>
                  </Select.Option>
                  <Select.Option value={DiscountType.FIXED_AMOUNT}>
                    <Space>
                      <MoneyCollectOutlined />
                      Giảm số tiền cố định
                    </Space>
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>
                      {discountType === DiscountType.PERCENTAGE ? 'Phần trăm giảm (%)' : 'Số tiền giảm (VND)'}
                    </span>
                  </Space>
                }
                name="discountValue"
                rules={[
                  { required: true, message: 'Vui lòng nhập giá trị giảm' },
                  { 
                    type: 'number', 
                    min: discountType === DiscountType.PERCENTAGE ? 1 : 1000, 
                    max: discountType === DiscountType.PERCENTAGE ? 100 : 10000000,
                    message: discountType === DiscountType.PERCENTAGE 
                      ? 'Phần trăm giảm phải từ 1% đến 100%' 
                      : 'Số tiền giảm phải từ 1,000 VND đến 10,000,000 VND'
                  },
                ]}
              >
                <InputNumber
                  placeholder={discountType === DiscountType.PERCENTAGE ? "VD: 20" : "VD: 50000"}
                  className="form-input"
                  style={{ width: '100%' }}
                  formatter={discountType === DiscountType.FIXED_AMOUNT ? 
                    value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : undefined
                  }
                  parser={discountType === DiscountType.FIXED_AMOUNT ? 
                    value => parseInt(value!.replace(/\$\s?|(,*)/g, '')) || 0 : undefined
                  }
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Giá trị đơn hàng tối thiểu (VND)</span>
                  </Space>
                }
                name="minOrderValue"
              >
                <InputNumber
                  placeholder="VD: 500000"
                  className="form-input"
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => Number(value!.replace(/\$\s?|(,*)/g, '')) as any}
                />
              </Form.Item>
            </Col>

            {discountType === DiscountType.PERCENTAGE && (
              <Col span={12}>
                <Form.Item
                  label={
                    <Space>
                      <DollarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                      <span>Số tiền giảm tối đa (VND)</span>
                    </Space>
                  }
                  name="maxDiscountAmount"
                  rules={[
                    { 
                      type: 'number', 
                      min: 1000, 
                      message: 'Số tiền giảm tối đa phải từ 1,000 VND'
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="VD: 100000"
                    className="form-input"
                    style={{ width: '100%' }}
                    min={0}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => Number(value!.replace(/\$\s?|(,*)/g, '')) as any}
                  />
                </Form.Item>
              </Col>
            )}
          </AntRow>

          {/* Usage Configuration */}
          <Divider orientation="left">
            <Space>
              <UserOutlined style={{ color: 'var(--primary-color)' }} />
              <span>Cấu hình sử dụng</span>
            </Space>
          </Divider>

          <AntRow gutter={[16, 12]}>
            <Col span={8}>
              <Form.Item
                label={
                  <Space>
                    <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Điểm cần để đổi</span>
                  </Space>
                }
                name="pointsRequired"
              >
                <InputNumber
                  placeholder="VD: 1000"
                  className="form-input"
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={
                  <Space>
                    <SettingOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Số lần sử dụng tối đa</span>
                  </Space>
                }
                name="usageLimit"
              >
                <InputNumber
                  placeholder="VD: 100"
                  className="form-input"
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={
                  <Space>
                    <UserOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Số lần 1 user có thể dùng</span>
                  </Space>
                }
                name="usagePerUser"
                rules={[{ type: 'number', min: 1, message: 'Phải ít nhất 1 lần' }]}
              >
                <InputNumber
                  placeholder="VD: 1"
                  className="form-input"
                  style={{ width: '100%' }}
                  min={1}
                />
              </Form.Item>
            </Col>
          </AntRow>

          {/* Time Configuration */}
          <Divider orientation="left">
            <Space>
              <CalendarOutlined style={{ color: 'var(--primary-color)' }} />
              <span>Thời gian hiệu lực</span>
            </Space>
          </Divider>

          <AntRow gutter={[16, 12]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Ngày bắt đầu</span>
                  </Space>
                }
                name="startDate"
                rules={[{ required: true, message: 'Vui lòng chọn ngày bắt đầu' }]}
              >
                <DatePicker
                  placeholder="Chọn ngày bắt đầu"
                  className="form-input"
                  style={{ width: '100%' }}
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <CalendarOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Ngày kết thúc</span>
                  </Space>
                }
                name="endDate"
                rules={[
                  { required: true, message: 'Vui lòng chọn ngày kết thúc' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || !getFieldValue('startDate')) {
                        return Promise.resolve();
                      }
                      if (value.isAfter(getFieldValue('startDate'))) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Ngày kết thúc phải sau ngày bắt đầu'));
                    },
                  }),
                ]}
              >
                <DatePicker
                  placeholder="Chọn ngày kết thúc"
                  className="form-input"
                  style={{ width: '100%' }}
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <SettingOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Trạng thái</span>
                  </Space>
                }
                name="isActive"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Hoạt động"
                  unCheckedChildren="Tạm dừng"
                />
              </Form.Item>
            </Col>
          </AntRow>

          <div className="modal-actions">
            <Button
              onClick={handleCancel}
              className="cancel-button"
              icon={<CloseOutlined />}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="save-button"
              icon={<PlusOutlined />}
              loading={loading}
            >
              Tạo voucher
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default CModalAddVoucher;